import { useState, useRef } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

import './ContentPanelHeader.css';
import ContentPanelMeta from './ContentPanelMeta';
import { tooltipSxElevated } from './jernyTooltipStyling';
import { useContentPanelContext } from '../../contexts/ContentPanelContext';
import noteOptionsPanelEngine from '../../pages/DashboardComponents/NoteOptionsPanel/Core/NoteOptionsPanelEngine';

/**
 * ContentPanelHeader
 * Displays entry title, service, meta and action buttons.
 * Copy title is a direct button.
 * MoreVert opens the NoteOptionsPanel via ContentPanelContext.
 *
 * collapsed is a controlled prop (owned by ContentPanelInner) — ContentPanelInner also
 * needs to know collapsed state to hide the AttachmentStrip, so it's the single source
 * of truth instead of local state here.
 *
 * @param {object} props
 * @param {object} props.entry
 * @param {boolean} props.isTemplate
 * @param {boolean} props.isJson
 * @param {string} props.viewMode
 * @param {string} props.bgMode
 * @param {string} [props.panelId='default'] - ADDED, needed so Pin toggle targets the right panel state (e.g. YourLibrary floating panels each have their own panelId)
 * @param {boolean} props.collapsed
 * @param {function} props.onToggleCollapse
 */
export default function ContentPanelHeader({ entry, isTemplate, isJson, viewMode, bgMode, panelId = 'default', collapsed, onToggleCollapse }) {
    const { openPanel } = useContentPanelContext();

    const [copiedTitle, setCopiedTitle] = useState(false);
    const moreButtonRef = useRef(null);

    /** Copies entry title to clipboard */
    const handleCopyTitle = (item) => {
        if (!item) return;
        navigator.clipboard.writeText(item).then(() => {
            setCopiedTitle(true);
            setTimeout(() => setCopiedTitle(false), 1500);
        });

        // if (!entry?.title) return;
        // navigator.clipboard.writeText(entry.title).then(() => {
        //     setCopiedTitle(true);
        //     setTimeout(() => setCopiedTitle(false), 1500);
        // });
    };

    /** Opens NoteOptionsPanel anchored below the more button */
    const handleOpenPanel = () => {
        if (moreButtonRef.current) {
            const rect = moreButtonRef.current.getBoundingClientRect();
            openPanel({ top: rect.bottom + 4, left: rect.right - 220 });
        } else {
            openPanel();
        }
    };

    /** Builds the title tooltip content — copy hint + remark (if set) */
    const getTitleTooltip = () => {
        if (copiedTitle) return 'Copied!';
        return (
            <>
                Copy Title to Clipboard
                {entry?.remark && (
                    <>
                        <br />
                        {entry.remark}
                    </>
                )}
            </>
        );
    };

    return (
        <Box id="content-panel-header-root" className="content-panel-header">
            <Box className="content-panel-header-title-row">
                <Box
                    sx={{ cursor: 'pointer' }}
                    className="content-panel-header-title-btn"
                    onClick={() => handleCopyTitle(entry.title)}
                    size="small"
                >
                    <Tooltip title={getTitleTooltip()} componentsProps={tooltipSxElevated}>
                        <Typography
                            className="content-panel-header-title"
                            sx={entry?.stateId === 1 ? { textDecoration: 'line-through', opacity: 0.5 } : {}}
                        >
                            {entry?.title || ''}
                        </Typography>
                    </Tooltip>
                </Box>

                {/* Collapse toggle */}
                <Tooltip title={collapsed ? 'Expand header' : 'Collapse header'} componentsProps={tooltipSxElevated}>
                    <IconButton className="content-panel-header-icon-btn" onClick={onToggleCollapse} size="small">
                        {collapsed ? <ExpandMoreIcon sx={{ fontSize: 14 }} /> : <ExpandLessIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                </Tooltip>

                {/* Copy title — direct action */}
                <Tooltip title={copiedTitle ? 'Copied!' : 'Copy RowKey to Clipboard'} componentsProps={tooltipSxElevated}>
                    <IconButton className="content-panel-header-icon-btn" onClick={() => handleCopyTitle(entry.rk)} size="small">
                        {copiedTitle ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                </Tooltip>

                {/* Pin toggle — direct engine call, no onX mutation prop. FIXED — passes the
                    live entry prop so the save is built from fresh data, not from the engine's
                    internal (possibly stale) copy. */}
                <Tooltip title={entry?.isPinned ? 'Unpin' : 'Pin'} componentsProps={tooltipSxElevated}>
                    <IconButton
                        className="content-panel-header-icon-btn"
                        onClick={() => noteOptionsPanelEngine.setPinned(entry, !entry?.isPinned, panelId)}
                        size="small"
                    >
                        {entry?.isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                </Tooltip>

                {/* More actions — opens NoteOptionsPanel via context */}
                <Tooltip title="Note options" componentsProps={tooltipSxElevated}>
                    <IconButton
                        ref={moreButtonRef}
                        className="content-panel-header-icon-btn"
                        size="small"
                        onClick={handleOpenPanel}
                    >
                        <MoreVertIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {!collapsed && (
                <>
                    <Typography className="content-panel-header-service">{entry?.service || ''}</Typography>
                    <ContentPanelMeta entry={entry ?? ''} />
                </>
            )}
        </Box>
    );
}
